import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import {
  Plus,
  PencilSimple,
  Trash,
  MagnifyingGlass,
  FilePdf,
  FileXls,
  CurrencyEur,
  Eye,
  CreditCard,
  Star,
  CaretDown,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import DatePickerDMY from '../components/DatePickerDMY';
import { openPdfInBrowser } from '../utils/pdfUtils';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHLY_CATEGORIES = ['201', '202', '203', '204', '205', '206'];
const CAPITAL_CATEGORIES = ['301', '302', '303', '304', '305', '306', '307', '308', '309', '310'];

const CATEGORY_TO_TYPE = {
  '201': 'other', '202': 'other', '203': 'other',
  '204': 'electricity', '205': 'other', '206': 'bank_fees',
  '301': 'other', '302': 'other', '303': 'other',
  '304': 'electrical_work', '305': 'other', '306': 'other',
  '307': 'other', '308': 'other', '309': 'bank_fees', '310': 'other',
};

const getCategoryColor = (cat) => {
  if (!cat || cat === 'uncategorized') return 'bg-zinc-50 text-zinc-700 border-zinc-200';
  const code = parseInt(cat);
  if (code >= 200 && code <= 206) return 'bg-blue-50 text-blue-700 border-blue-200';
  if (code >= 300 && code <= 310) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-zinc-50 text-zinc-700 border-zinc-200';
};

export default function Expenses() {
  const { t } = useLanguage();
  const { isAdmin } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterBuilding, setFilterBuilding] = useState('all');
  // Phase 715 — Multi-select category filter. Empty array = "no filter,
  // show every category" (matches the legacy "all" behaviour). Each
  // toggled checkbox in the sidebar adds/removes the category code.
  const [filterCategories, setFilterCategories] = useState([]);
  const [filterGroup, setFilterGroup] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all');
  // Phase 716 — Explicit Date From / Date To filters so the user can scope
  // any period (year, quarter, custom range) and the grid + PDF + Excel
  // all stay in sync. When both Date From/To and Month filter are set,
  // they are AND-ed together.
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [viewMode, setViewMode] = useState('group');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [customCategories, setCustomCategories] = useState({});

  // Phase 717 — Saved Views (per-user filter presets).
  // `savedViews`           list of {view_id, name, filters, is_default, ...}
  // `activeViewId`         currently loaded view (null = ad-hoc / "no view")
  // `viewModified`         user mutated filters after loading the view
  //                        (computed by deep-comparing current filters
  //                        against the loaded view's stored filters).
  // `initialDefaultLoadedRef` guards against double-loading the default
  //                        view (StrictMode double-mount, fast hot reload).
  const [savedViews, setSavedViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [viewModified, setViewModified] = useState(false);
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [saveViewName, setSaveViewName] = useState('');
  const [saveViewDefault, setSaveViewDefault] = useState(false);
  const [renameViewOpen, setRenameViewOpen] = useState(false);
  const [renameViewName, setRenameViewName] = useState('');
  const [deleteViewConfirmOpen, setDeleteViewConfirmOpen] = useState(false);
  const initialDefaultLoadedRef = useRef(false);

  // Expense Add/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  // Phase 412 — Service Period inside the Add/Edit dialog is hidden by
  // default; users click "Add Billing Period" to reveal the Date From / To
  // inputs. When editing an expense that already has a billing period it
  // auto-opens. Closing the dialog resets the state.
  const [showBillingPeriod, setShowBillingPeriod] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [formData, setFormData] = useState({
    building_id: '', expense_type: 'other', category: '204',
    amount: 0, date: new Date().toISOString().split('T')[0],
    notes: '', billing_period_start: '', billing_period_end: '',
    electricity_paid_dates: [], payment_source: 'none',
    // Phase 418 — Optional VAT breakdown + standalone Description.
    net_amount: '', vat_amount: '', description: '',
  });
  const [newPaidDate, setNewPaidDate] = useState('');

  // Delete expense dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingExpense, setDeletingExpense] = useState(null);

  // Category management
  const [renameCatDialogOpen, setRenameCatDialogOpen] = useState(false);
  const [renamingCat, setRenamingCat] = useState('');
  const [renameCatValue, setRenameCatValue] = useState('');
  const [deleteCatDialogOpen, setDeleteCatDialogOpen] = useState(false);
  const [deletingCat, setDeletingCat] = useState('');
  const [moveCatTarget, setMoveCatTarget] = useState('');
  const [manageCatOpen, setManageCatOpen] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatGroup, setNewCatGroup] = useState('monthly');
  const [allCategories, setAllCategories] = useState([]);

  // Payment dialogs
  const [addPaymentDialogOpen, setAddPaymentDialogOpen] = useState(false);
  const [viewPaymentsDialogOpen, setViewPaymentsDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', date: new Date().toISOString().split('T')[0], notes: '', payment_source: 'cash' });
  const [expensePayments, setExpensePayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);

  // Delete payment dialog
  const [deletePaymentDialogOpen, setDeletePaymentDialogOpen] = useState(false);
  const [deletingPayment, setDeletingPayment] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterBuilding !== 'all') params.append('building_id', filterBuilding);
      // Phase 715 — Send the multi-select categories as a single
      // comma-separated query param so the backend can `$in`-match.
      if (filterCategories.length > 0) params.append('categories', filterCategories.join(','));
      if (filterGroup !== 'all') params.append('category_group', filterGroup);
      // Phase 716 — Server-side date range filter.
      if (filterDateFrom) params.append('date_from', filterDateFrom);
      if (filterDateTo) params.append('date_to', filterDateTo);

      const qs = params.toString() ? `?${params.toString()}` : '';
      const summaryQs = filterBuilding !== 'all' ? `?building_id=${filterBuilding}` : '';

      const [expensesRes, buildingsRes, summaryRes, catRes] = await Promise.all([
        axios.get(`${API}/expenses${qs}`),
        axios.get(`${API}/buildings`),
        axios.get(`${API}/expenses/summary${summaryQs}`),
        axios.get(`${API}/expense-categories`),
      ]);
      setExpenses(expensesRes.data);
      setBuildings(buildingsRes.data);
      setSummary(summaryRes.data);
      const catMap = {};
      catRes.data.forEach(c => { catMap[c.category_code] = c.name; });
      setCustomCategories(catMap);
      // Build full category list (hardcoded + custom)
      const customCats = catRes.data.filter(c => c.is_custom);
      const customMonthlyCodes = customCats.filter(c => c.group === 'monthly').map(c => c.category_code);
      const customCapitalCodes = customCats.filter(c => c.group === 'capital').map(c => c.category_code);
      setAllCategories([
        ...MONTHLY_CATEGORIES, ...customMonthlyCodes,
        ...CAPITAL_CATEGORIES, ...customCapitalCodes,
      ]);
    } catch {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [filterBuilding, filterCategories, filterGroup, filterDateFrom, filterDateTo, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Phase 717 — Saved Views: fetch on mount + auto-load default once.
  const fetchSavedViews = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/expenses/saved-views`);
      setSavedViews(r.data || []);
      return r.data || [];
    } catch {
      return [];
    }
  }, []);

  const applyViewFilters = useCallback((view) => {
    if (!view || !view.filters) return;
    const f = view.filters;
    setFilterBuilding(f.building_id || 'all');
    setFilterGroup(f.category_group || 'all');
    setFilterCategories(Array.isArray(f.categories) ? f.categories : []);
    setFilterMonth(f.month || 'all');
    setFilterDateFrom(f.date_from || '');
    setFilterDateTo(f.date_to || '');
    setSearch(f.search || '');
    setActiveViewId(view.view_id);
  }, []);

  useEffect(() => {
    (async () => {
      const views = await fetchSavedViews();
      if (!initialDefaultLoadedRef.current) {
        initialDefaultLoadedRef.current = true;
        const def = (views || []).find(v => v.is_default);
        if (def) applyViewFilters(def);
      }
    })();
  }, [fetchSavedViews, applyViewFilters]);

  // Whenever ANY filter changes while a view is loaded, compare against
  // the view's stored filters. Equal → clear `viewModified`; different →
  // set `viewModified=true`. This eliminates the race we had with the
  // `applyingViewRef` flag (React batches state updates across renders
  // and the timeout could fire too early, causing freshly-loaded views
  // to read as "Modified" right after mount/reload).
  useEffect(() => {
    if (!activeViewId) {
      setViewModified(false);
      return;
    }
    const av = savedViews.find(v => v.view_id === activeViewId);
    if (!av) return;
    const cur = {
      building_id:    filterBuilding !== 'all' ? filterBuilding : null,
      category_group: filterGroup !== 'all' ? filterGroup : null,
      categories:     [...filterCategories].sort(),
      date_from:      filterDateFrom || null,
      date_to:        filterDateTo || null,
      month:          filterMonth !== 'all' ? filterMonth : null,
      search:         search || null,
    };
    const saved = av.filters || {};
    const savedNorm = {
      building_id:    saved.building_id || null,
      category_group: saved.category_group || null,
      categories:     [...(saved.categories || [])].sort(),
      date_from:      saved.date_from || null,
      date_to:        saved.date_to || null,
      month:          saved.month || null,
      search:         saved.search || null,
    };
    setViewModified(JSON.stringify(cur) !== JSON.stringify(savedNorm));
  }, [filterBuilding, filterCategories, filterGroup, filterMonth, filterDateFrom, filterDateTo, search, activeViewId, savedViews]);

  const collectCurrentFilters = useCallback(() => ({
    building_id:    filterBuilding !== 'all' ? filterBuilding : null,
    category_group: filterGroup !== 'all' ? filterGroup : null,
    categories:     filterCategories,
    date_from:      filterDateFrom || null,
    date_to:        filterDateTo || null,
    month:          filterMonth !== 'all' ? filterMonth : null,
    search:         search || null,
  }), [filterBuilding, filterGroup, filterCategories, filterDateFrom, filterDateTo, filterMonth, search]);

  const saveCurrentAsView = async () => {
    const name = (saveViewName || '').trim();
    if (!name) {
      toast.error(t('viewNameRequired') || 'Please enter a view name');
      return;
    }
    try {
      const r = await axios.post(`${API}/expenses/saved-views`, {
        name,
        filters: collectCurrentFilters(),
        is_default: !!saveViewDefault,
      });
      toast.success(t('viewSaved') || 'View saved');
      setSaveViewOpen(false);
      setSaveViewName('');
      setSaveViewDefault(false);
      const views = await fetchSavedViews();
      const fresh = views.find(v => v.view_id === r.data?.view_id);
      if (fresh) {
        setActiveViewId(fresh.view_id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || (t('errorOccurred') || 'Error'));
    }
  };

  const updateCurrentView = async () => {
    if (!activeViewId) return;
    try {
      await axios.put(`${API}/expenses/saved-views/${activeViewId}`, {
        filters: collectCurrentFilters(),
      });
      toast.success(t('viewUpdated') || 'View updated');
      setViewModified(false);
      fetchSavedViews();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (t('errorOccurred') || 'Error'));
    }
  };

  const renameActiveView = async () => {
    const name = (renameViewName || '').trim();
    if (!activeViewId || !name) return;
    try {
      await axios.put(`${API}/expenses/saved-views/${activeViewId}`, { name });
      toast.success(t('viewRenamed') || 'View renamed');
      setRenameViewOpen(false);
      fetchSavedViews();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (t('errorOccurred') || 'Error'));
    }
  };

  const deleteActiveView = async () => {
    if (!activeViewId) return;
    setDeleteViewConfirmOpen(true);
  };

  const confirmDeleteActiveView = async () => {
    if (!activeViewId) {
      setDeleteViewConfirmOpen(false);
      return;
    }
    try {
      await axios.delete(`${API}/expenses/saved-views/${activeViewId}`);
      toast.success(t('viewDeleted') || 'View deleted');
      setActiveViewId(null);
      setViewModified(false);
      fetchSavedViews();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (t('errorOccurred') || 'Error'));
    } finally {
      setDeleteViewConfirmOpen(false);
    }
  };

  const toggleDefaultForActive = async () => {
    if (!activeViewId) return;
    const cur = savedViews.find(v => v.view_id === activeViewId);
    if (!cur) return;
    try {
      await axios.put(`${API}/expenses/saved-views/${activeViewId}`, { is_default: !cur.is_default });
      toast.success(cur.is_default ? (t('defaultRemoved') || 'Default removed') : (t('setAsDefault') || 'Set as default'));
      fetchSavedViews();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (t('errorOccurred') || 'Error'));
    }
  };

  const activeView = savedViews.find(v => v.view_id === activeViewId) || null;

  const getCatLabel = (cat) => {
    if (!cat) return t('uncategorized');
    if (customCategories[cat]) return customCategories[cat];
    const key = `cat_${cat}`;
    const label = t(key);
    return label !== key ? label : cat;
  };

  // ---- Expense CRUD ----
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Phase 418 — Normalize Net/VAT/Description so the backend stores them
      // as numbers (or null when blank) and never receives empty strings.
      const netVal = formData.net_amount === '' || formData.net_amount == null ? null : (parseFloat(formData.net_amount) || 0);
      const vatVal = formData.vat_amount === '' || formData.vat_amount == null ? null : (parseFloat(formData.vat_amount) || 0);
      // If user typed both Net & VAT, derive the Total automatically. Otherwise
      // honour whatever they typed in the Total field.
      let totalAmount = parseFloat(formData.amount) || 0;
      if (netVal != null && vatVal != null) {
        totalAmount = Math.round((netVal + vatVal) * 100) / 100;
      }
      const payload = {
        ...formData,
        amount: totalAmount,
        net_amount: netVal,
        vat_amount: vatVal,
        description: formData.description || '',
        expense_type: CATEGORY_TO_TYPE[formData.category] || 'other',
        billing_period_start: formData.billing_period_start || null,
        billing_period_end: formData.billing_period_end || null,
        electricity_paid_dates: formData.category === '204' ? formData.electricity_paid_dates : null,
        status: 'unpaid',
        paid_date: null,
      };
      // Phase 371 — Allow payment_source to flow through on EDIT so users can
      // switch Cash↔Bank↔Reserve-Fund without having to delete & re-create the
      // expense. The backend's `update_expense` resyncs the ledger
      // automatically (`_resync_expense_ledger`). On CREATE we still strip it
      // here because a separate explicit POST to /payments is made below.
      if (!editingExpense) {
        delete payload.payment_source;
      }

      if (editingExpense) {
        // Only update non-amount fields for existing expenses. Net / VAT
        // are tied to amount so they're also locked on edit.
        const { amount, net_amount, vat_amount, ...editPayload } = payload;
        await axios.put(`${API}/expenses/${editingExpense.expense_id}`, editPayload);
      } else {
        const res = await axios.post(`${API}/expenses`, payload);
        // If payment source selected, auto-create payment
        if (formData.payment_source !== 'none' && res.data?.expense_id) {
          const expAmount = parseFloat(formData.amount) || 0;
          if (expAmount > 0) {
            await axios.post(`${API}/expenses/${res.data.expense_id}/payments`, {
              amount: expAmount,
              date: formData.date,
              notes: '',
              payment_source: formData.payment_source,
            });
          }
        }
      }
      toast.success(t('savedSuccess'));
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/expenses/${deletingExpense.expense_id}`);
      toast.success(t('deletedSuccess'));
      setDeleteDialogOpen(false);
      setDeletingExpense(null);
      fetchData();
    } catch {
      toast.error(t('errorOccurred'));
    }
  };

  const resetForm = () => {
    setEditingExpense(null);
    setFormData({
      building_id: '', expense_type: 'other', category: '204',
      amount: 0, date: new Date().toISOString().split('T')[0],
      notes: '', billing_period_start: '', billing_period_end: '',
      electricity_paid_dates: [], payment_source: 'none',
      net_amount: '', vat_amount: '', description: '',
    });
    setNewPaidDate('');
    setShowBillingPeriod(false); // Phase 412 — collapse the Service Period section.
  };

  const openAddDialog = () => { resetForm(); setDialogOpen(true); };

  const openEditDialog = (expense) => {
    setEditingExpense(expense);
    setFormData({
      building_id: expense.building_id, expense_type: expense.expense_type,
      category: expense.category || '', amount: expense.amount,
      date: expense.date, notes: expense.notes || '',
      billing_period_start: expense.billing_period_start || '',
      billing_period_end: expense.billing_period_end || '',
      electricity_paid_dates: expense.electricity_paid_dates || [],
      net_amount: expense.net_amount != null ? expense.net_amount : '',
      vat_amount: expense.vat_amount != null ? expense.vat_amount : '',
      description: expense.description || '',
    });
    // Phase 412 — Auto-expand the Service Period section if the expense
    // being edited already has values stored.
    setShowBillingPeriod(!!(expense.billing_period_start || expense.billing_period_end));
    setDialogOpen(true);
  };

  const addPaidDateEntry = () => {
    if (newPaidDate && !formData.electricity_paid_dates.includes(newPaidDate)) {
      setFormData({ ...formData, electricity_paid_dates: [...formData.electricity_paid_dates, newPaidDate].sort() });
      setNewPaidDate('');
    }
  };

  const removePaidDateEntry = (d) => {
    setFormData({ ...formData, electricity_paid_dates: formData.electricity_paid_dates.filter(x => x !== d) });
  };

  // ---- Category Management ----
  const openRenameCat = (cat) => { setRenamingCat(cat); setRenameCatValue(getCatLabel(cat)); setRenameCatDialogOpen(true); };
  const handleRenameCat = async () => {
    try {
      await axios.put(`${API}/expense-categories/${renamingCat}`, { name: renameCatValue });
      toast.success(t('savedSuccess'));
      setRenameCatDialogOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || t('errorOccurred')); }
  };
  const openDeleteCat = (cat) => { setDeletingCat(cat); setMoveCatTarget(''); setDeleteCatDialogOpen(true); };
  const handleDeleteCat = async () => {
    try {
      const params = moveCatTarget && moveCatTarget !== 'none' ? `?move_to=${moveCatTarget}` : '';
      await axios.delete(`${API}/expense-categories/${deletingCat}${params}`);
      toast.success(t('deletedSuccess'));
      setDeleteCatDialogOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || t('errorOccurred')); }
  };
  const handleAddCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      await axios.post(`${API}/expense-categories`, { name: newCatName.trim(), group: newCatGroup });
      toast.success(t('savedSuccess'));
      setNewCatName('');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || t('errorOccurred')); }
  };

  // ---- Payment CRUD ----
  const openAddPayment = (expense) => {
    setSelectedExpense(expense);
    setPaymentForm({ amount: '', date: new Date().toISOString().split('T')[0], notes: '', payment_source: 'cash' });
    setAddPaymentDialogOpen(true);
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    if (!selectedExpense) return;
    const amount = parseFloat(paymentForm.amount);
    if (!amount || amount <= 0) {
      toast.error(t('amount') + ' > 0');
      return;
    }
    try {
      await axios.post(`${API}/expenses/${selectedExpense.expense_id}/payments`, {
        amount, date: paymentForm.date, notes: paymentForm.notes, payment_source: paymentForm.payment_source,
      });
      toast.success(t('savedSuccess'));
      setAddPaymentDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const openViewPayments = async (expense) => {
    setSelectedExpense(expense);
    setViewPaymentsDialogOpen(true);
    setLoadingPayments(true);
    try {
      const res = await axios.get(`${API}/expenses/${expense.expense_id}/payments`);
      setExpensePayments(res.data);
    } catch {
      toast.error(t('errorOccurred'));
    } finally {
      setLoadingPayments(false);
    }
  };

  const confirmDeletePayment = (payment) => {
    setDeletingPayment(payment);
    setDeletePaymentDialogOpen(true);
  };

  const handleDeletePayment = async () => {
    if (!deletingPayment) return;
    try {
      await axios.delete(`${API}/expense-payments/${deletingPayment.payment_id}`);
      toast.success(t('deletedSuccess'));
      setDeletePaymentDialogOpen(false);
      setDeletingPayment(null);
      // Refresh payments list in dialog
      if (selectedExpense) {
        const res = await axios.get(`${API}/expenses/${selectedExpense.expense_id}/payments`);
        setExpensePayments(res.data);
      }
      fetchData();
    } catch {
      toast.error(t('errorOccurred'));
    }
  };

  // ---- Filtering ----
  const filteredExpenses = expenses.filter(e => {
    const matchesSearch = e.building_name?.toLowerCase().includes(search.toLowerCase()) ||
      e.notes?.toLowerCase().includes(search.toLowerCase()) ||
      getCatLabel(e.category)?.toLowerCase().includes(search.toLowerCase());
    if (!matchesSearch) return false;
    if (filterMonth !== 'all' && e.date) {
      if (e.date.substring(0, 7) !== filterMonth) return false;
    }
    return true;
  });

  const availableMonths = [...new Set(expenses.map(e => e.date?.substring(0, 7)).filter(Boolean))].sort().reverse();

  // Grouped view
  const groupedByCategory = {};
  filteredExpenses.forEach(e => {
    const cat = e.category || 'uncategorized';
    if (!groupedByCategory[cat]) groupedByCategory[cat] = { expenses: [], total: 0 };
    groupedByCategory[cat].expenses.push(e);
    groupedByCategory[cat].total += e.amount || 0;
  });
  const sortedCategoryKeys = Object.keys(groupedByCategory).sort((a, b) => a.localeCompare(b));

  const toggleCategory = (cat) => setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  const toggleAllCategories = (expand) => {
    const next = {};
    sortedCategoryKeys.forEach(cat => { next[cat] = expand; });
    setExpandedCategories(next);
  };

  // Phase 393 — View PDF: opens the SAME PDF used for downloads + email
  // attachments (`/api/expenses/export/pdf`), with the user's current
  // filters applied so the preview matches what they're looking at.
  // Filters honoured: Building, Category Group, Category, Month (→
  // date_from/date_to). The Excel export was retired in this phase.
  const [pdfLoading, setPdfLoading] = useState(false);
  // Phase 715/716 — Build the common params object used by BOTH the PDF
  // and Excel exports so the downloaded file is ALWAYS a 1:1 copy of the
  // currently filtered grid. Date From / Date To take precedence over
  // the Month dropdown when both are set.
  const buildExportParams = () => {
    const params = {};
    if (filterBuilding !== 'all') params.building_id = filterBuilding;
    if (filterCategories.length > 0) params.categories = filterCategories.join(',');
    if (filterGroup !== 'all') params.category_group = filterGroup;
    if (filterDateFrom) params.date_from = filterDateFrom;
    if (filterDateTo) params.date_to = filterDateTo;
    // Month picker stays supported as a quick "single calendar month"
    // selector but only when no explicit From/To range is in play.
    if (!filterDateFrom && !filterDateTo && filterMonth !== 'all') {
      const [yr, mo] = filterMonth.split('-').map(Number);
      if (yr && mo) {
        const last = new Date(yr, mo, 0).getDate();
        params.date_from = `${filterMonth}-01`;
        params.date_to = `${filterMonth}-${String(last).padStart(2, '0')}`;
      }
    }
    return params;
  };

  const viewPdf = () => {
    try {
      setPdfLoading(true);
      openPdfInBrowser('/expenses/export/pdf', buildExportParams());
    } catch (e) {
      toast.error(t('previewFailed') || 'Preview failed');
    } finally {
      setTimeout(() => setPdfLoading(false), 400);
    }
  };

  // Phase 716 — Excel export. Reuses the same buildExportParams() so the
  // spreadsheet always contains exactly what the user sees on screen.
  const exportExcel = () => {
    try {
      const token = localStorage.getItem('cleanpro_token') || '';
      const params = { ...buildExportParams(), token };
      const qs = new URLSearchParams(params).toString();
      const url = `${API}/expenses/export/excel?${qs}`;
      // Trigger download in a new tab so the browser's native handler
      // picks the .xlsx response correctly.
      window.open(url, '_blank');
    } catch (e) {
      toast.error(t('errorOccurred') || 'Error');
    }
  };

  // ---- Action buttons for each expense row ----
  const ExpenseActions = ({ expense }) => (
    <div className="flex gap-1">
      <button
        onClick={() => openAddPayment(expense)}
        className="p-2 hover:bg-blue-50 text-blue-600 transition-colors"
        title={t('addPayment')}
        data-testid={`add-payment-${expense.expense_id}`}
      >
        <CreditCard size={18} />
      </button>
      <button
        onClick={() => openViewPayments(expense)}
        className="p-2 hover:bg-zinc-100 text-zinc-600 transition-colors"
        title={t('viewPayments')}
        data-testid={`view-payments-${expense.expense_id}`}
      >
        <Eye size={18} />
      </button>
      <button
        onClick={() => openEditDialog(expense)}
        className="p-2 hover:bg-zinc-100 transition-colors"
        title={t('edit')}
        data-testid={`edit-expense-${expense.expense_id}`}
      >
        <PencilSimple size={18} />
      </button>
      {isAdmin && (
        <button
          onClick={() => { setDeletingExpense(expense); setDeleteDialogOpen(true); }}
          className="p-2 hover:bg-red-50 text-red-600 transition-colors"
          title={t('delete')}
          data-testid={`delete-expense-${expense.expense_id}`}
        >
          <Trash size={18} />
        </button>
      )}
    </div>
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-zinc-500">{t('loading')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="expenses-page">
        {/* Phase 434 — Page header clarifying scope vs Reference Categories */}
        <div className="mb-5">
          <h1 className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
            {t('expensesTitle') || 'Expenses'}
          </h1>
          <p className="text-xs text-zinc-500 mt-1 max-w-3xl" data-testid="expenses-page-desc">
            {t('expensesDesc') || 'Official company expenses used for VAT and accounting reporting. Only records here (with a receipt or invoice and VAT evidence) feed into VAT reports — Reference Categories and bank movements do not.'}
          </p>
        </div>

        {/* Summary — Phase 715/716: when filters are active, the card reflects the
            filtered/visible expenses (matches the table + PDF + Excel totals).
            When no filters are applied it falls back to the server-side summary. */}
        {summary && (() => {
          const filtersActive = filterCategories.length > 0 || filterGroup !== 'all' || filterMonth !== 'all' || !!filterDateFrom || !!filterDateTo || filterBuilding !== 'all';
          const visibleTotal = filteredExpenses.reduce((s, e) => s + (e.amount || 0), 0);
          const shownTotal = filtersActive ? visibleTotal : summary.total;
          const shownCount = filtersActive ? filteredExpenses.length : summary.count;
          return (
            <div className="mb-6">
              <div className="bg-white border border-zinc-200 p-4 max-w-md" data-testid="total-expenses-card">
                <p className="text-sm text-zinc-500">{t('totalExpenses')}</p>
                <p className="text-2xl font-bold font-mono mt-1">&euro;{shownTotal.toFixed(2)}</p>
                <p className="text-xs text-zinc-400 mt-1">
                  {shownCount} {t('expenses').toLowerCase()}
                  {filtersActive && <span className="ml-1 text-zinc-500">· {t('filtered') || 'filtered'}</span>}
                </p>
              </div>
            </div>
          );
        })()}

        {/* Filters (View toggle + Category Breakdown card removed in Phase 418) */}
        <div className="flex flex-col gap-3 mb-6">

          <div className="flex flex-col sm:flex-row flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input type="text" placeholder={t('search') + '...'} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 border-zinc-200 rounded-none" data-testid="search-input" />
            </div>
            <Select value={filterBuilding} onValueChange={setFilterBuilding}>
              <SelectTrigger className="w-[180px] rounded-none" data-testid="building-filter"><SelectValue placeholder={t('buildings')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all')} {t('buildings')}</SelectItem>
                {buildings.map(b => <SelectItem key={b.building_id} value={b.building_id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterGroup} onValueChange={(val) => { setFilterGroup(val); setFilterCategories([]); }}>
              <SelectTrigger className="w-[170px] rounded-none" data-testid="group-filter"><SelectValue placeholder={t('categoryGroup')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('allGroups')}</SelectItem>
                <SelectItem value="monthly">{t('monthlyExpenses')}</SelectItem>
                <SelectItem value="capital">{t('capitalExpenses')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterMonth} onValueChange={setFilterMonth}>
              <SelectTrigger className="w-[160px] rounded-none" data-testid="month-filter"><SelectValue placeholder={t('allMonths')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('allMonths')}</SelectItem>
                {availableMonths.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            {/* Phase 716 — Explicit Date From / Date To inputs. When both are
                empty the Month dropdown is still honoured; when either is
                set it takes precedence over the Month picker. */}
            <Input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              className="w-[160px] rounded-none border-zinc-200"
              placeholder={t('dateFrom') || 'From'}
              title={t('dateFrom') || 'Date From'}
              data-testid="expenses-date-from"
            />
            <Input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              className="w-[160px] rounded-none border-zinc-200"
              placeholder={t('dateTo') || 'To'}
              title={t('dateTo') || 'Date To'}
              data-testid="expenses-date-to"
            />
            {/* Phase 717 — Saved Views dropdown + Save Current View action. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="border-zinc-200 rounded-none"
                  data-testid="expenses-saved-views-trigger"
                >
                  <Star size={18} className="mr-2" weight={activeView ? 'fill' : 'regular'} />
                  {t('savedViews') || 'Saved Views'}
                  <CaretDown size={14} className="ml-2 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72 rounded-none">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-zinc-500">
                  {t('mySavedViews') || 'My Saved Views'}
                </DropdownMenuLabel>
                {savedViews.length === 0 && (
                  <div className="px-3 py-2 text-xs text-zinc-400" data-testid="expenses-saved-views-empty">
                    {t('noSavedViewsYet') || 'No saved views yet'}
                  </div>
                )}
                {savedViews.map(v => (
                  <DropdownMenuItem
                    key={v.view_id}
                    onSelect={() => applyViewFilters(v)}
                    data-testid={`saved-view-${v.view_id}`}
                    className={`flex items-center justify-between gap-2 ${activeViewId === v.view_id ? 'bg-zinc-100 font-semibold' : ''}`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <Star size={14} weight={v.is_default ? 'fill' : 'regular'} className={v.is_default ? 'text-amber-500' : 'text-zinc-400'} />
                      <span className="truncate">{v.name}</span>
                    </span>
                    {v.is_default && (
                      <span className="text-[9px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5">
                        {t('default') || 'Default'}
                      </span>
                    )}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => { setSaveViewName(''); setSaveViewDefault(false); setSaveViewOpen(true); }}
                  data-testid="expenses-save-view-action"
                >
                  <Plus size={14} className="mr-2" />
                  {t('saveCurrentView') || 'Save Current View'}
                </DropdownMenuItem>
                {activeView && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={updateCurrentView} data-testid="expenses-update-view-action">
                      <PencilSimple size={14} className="mr-2" />
                      {t('updateWithCurrentFilters') || 'Update with Current Filters'}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => { setRenameViewName(activeView.name); setRenameViewOpen(true); }} data-testid="expenses-rename-view-action">
                      <PencilSimple size={14} className="mr-2" />
                      {t('renameView') || 'Rename'}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={toggleDefaultForActive} data-testid="expenses-toggle-default-action">
                      <Star size={14} className="mr-2" weight={activeView.is_default ? 'fill' : 'regular'} />
                      {activeView.is_default ? (t('removeDefault') || 'Remove default') : (t('setAsDefault') || 'Set as default')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={deleteActiveView} className="text-red-600 focus:text-red-700" data-testid="expenses-delete-view-action">
                      <Trash size={14} className="mr-2" />
                      {t('deleteView') || 'Delete'}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              className="border-zinc-200 rounded-none"
              onClick={viewPdf}
              disabled={pdfLoading}
              data-testid="view-pdf-btn"
            >
              <FilePdf size={18} className="mr-2" />
              {pdfLoading ? (t('generating') || 'Generating…') : (t('viewPdf') || 'View PDF')}
            </Button>
            {/* Phase 716 — Excel export (mirrors PDF filter behaviour). */}
            <Button
              variant="outline"
              className="border-zinc-200 rounded-none"
              onClick={exportExcel}
              data-testid="export-excel-btn"
            >
              <FileXls size={18} className="mr-2" />
              {t('exportExcel') || 'Export Excel'}
            </Button>
            {isAdmin && (
              <Button onClick={() => setManageCatOpen(true)} variant="outline" className="border-zinc-200 rounded-none" data-testid="manage-categories-btn">
                <PencilSimple size={18} className="mr-2" />{t('manageCategories')}
              </Button>
            )}
            <Button onClick={openAddDialog} className="bg-zinc-950 rounded-none" data-testid="add-expense-btn">
              <Plus size={18} className="mr-2" />{t('addExpense')}
            </Button>
          </div>

          {/* Phase 716 — Active filter chips. Each chip is individually
              removable. The "Clear all filters" button resets every filter
              in one click and immediately refreshes the grid + summary. */}
          {(() => {
            const chips = [];
            if (filterBuilding !== 'all') {
              const bld = buildings.find(b => b.building_id === filterBuilding);
              chips.push({
                key: 'building',
                label: `${t('building') || 'Building'}: ${bld?.name || filterBuilding}`,
                onRemove: () => setFilterBuilding('all'),
              });
            }
            if (filterGroup !== 'all') {
              chips.push({
                key: 'group',
                label: filterGroup === 'monthly' ? (t('monthlyExpenses') || 'Monthly') : (t('capitalExpenses') || 'Capital'),
                onRemove: () => setFilterGroup('all'),
              });
            }
            filterCategories.forEach(cat => {
              chips.push({
                key: `cat-${cat}`,
                label: getCatLabel(cat),
                onRemove: () => setFilterCategories(prev => prev.filter(c => c !== cat)),
              });
            });
            if (filterMonth !== 'all') {
              chips.push({
                key: 'month',
                label: `${t('month') || 'Month'}: ${filterMonth}`,
                onRemove: () => setFilterMonth('all'),
              });
            }
            if (filterDateFrom || filterDateTo) {
              const toDmy = (s) => {
                if (!s || s.length < 10) return s || '';
                return `${s.substring(8,10)}/${s.substring(5,7)}/${s.substring(0,4)}`;
              };
              chips.push({
                key: 'date-range',
                label: `${toDmy(filterDateFrom) || '…'} → ${toDmy(filterDateTo) || '…'}`,
                onRemove: () => { setFilterDateFrom(''); setFilterDateTo(''); },
              });
            }
            if (search) {
              chips.push({
                key: 'search',
                label: `${t('search') || 'Search'}: "${search}"`,
                onRemove: () => setSearch(''),
              });
            }
            if (chips.length === 0 && !activeView) return null;
            const clearAll = () => {
              setFilterBuilding('all');
              setFilterGroup('all');
              setFilterCategories([]);
              setFilterMonth('all');
              setFilterDateFrom('');
              setFilterDateTo('');
              setSearch('');
              setActiveViewId(null);
              setViewModified(false);
            };
            return (
              <div className="flex flex-wrap items-center gap-2" data-testid="expenses-active-filter-chips">
                {activeView && (
                  <span
                    className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-900 text-[11px] px-2 py-1"
                    data-testid="expenses-active-view-badge"
                    title={activeView.name}
                  >
                    <Star size={12} weight="fill" className="text-amber-500" />
                    <span className="font-semibold">{activeView.name}</span>
                    {viewModified && (
                      <span className="ml-1 text-amber-700/80" data-testid="expenses-active-view-modified">
                        · {t('modified') || 'Modified'}
                      </span>
                    )}
                  </span>
                )}
                <span className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">
                  {chips.length > 0 ? (t('activeFilters') || 'Active filters') + ':' : (t('noFiltersApplied') || 'No filters applied')}
                </span>
                {chips.map(chip => (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={chip.onRemove}
                    className="inline-flex items-center gap-1.5 bg-zinc-900 text-white text-[11px] px-2 py-1 hover:bg-zinc-700 transition-colors"
                    data-testid={`filter-chip-${chip.key}`}
                    title={t('removeFilter') || 'Remove filter'}
                  >
                    <span>{chip.label}</span>
                    <span className="text-white/70 hover:text-white text-sm leading-none">&times;</span>
                  </button>
                ))}
                {(chips.length > 0 || activeView) && (
                  <button
                    type="button"
                    onClick={clearAll}
                    className="text-[11px] underline text-zinc-600 hover:text-zinc-900 ml-1"
                    data-testid="expenses-clear-all-filters"
                  >
                    {t('clearAllFilters') || 'Clear all'}
                  </button>
                )}
              </div>
            );
          })()}
        </div>

        {/* Phase 418 — Sidebar (categories) + Main table (all expenses) */}
        <div className="flex flex-col lg:flex-row gap-4">

          {/* CATEGORIES SIDEBAR — Phase 715: multi-select checkbox list.
              An empty selection means "show every category" (the legacy
              "All" behaviour). Each row toggles independently. */}
          <aside className="w-full lg:w-64 lg:shrink-0" data-testid="expenses-categories-sidebar">
            <div className="bg-white border border-zinc-200 sticky top-4">
              <div className="px-3 py-2 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between gap-2">
                <h3 className="text-[11px] font-semibold uppercase tracking-wider text-zinc-700">{t('expenseCategories') || 'Categories'}</h3>
                {filterCategories.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setFilterCategories([])}
                    className="text-[10px] underline text-zinc-500 hover:text-zinc-900"
                    data-testid="expenses-cat-clear"
                  >
                    {t('clearAll') || 'Clear'}
                  </button>
                )}
              </div>
              <div className="max-h-[60vh] overflow-y-auto">
                <button
                  type="button"
                  onClick={() => setFilterCategories([])}
                  data-testid="expenses-cat-all"
                  className={`w-full flex items-center justify-between px-3 py-2 text-xs text-left border-l-2 transition-colors ${filterCategories.length === 0
                    ? 'border-l-zinc-900 bg-zinc-100 text-zinc-900 font-semibold'
                    : 'border-l-transparent hover:bg-zinc-50 text-zinc-700'}`}
                >
                  <span>{t('allCategories') || 'All'}</span>
                  <span className="font-mono text-zinc-500">{expenses.length}</span>
                </button>
                {(() => {
                  const catTotals = {};
                  expenses.forEach(e => {
                    const k = e.category || 'uncategorized';
                    if (!catTotals[k]) catTotals[k] = { count: 0, total: 0 };
                    catTotals[k].count += 1;
                    catTotals[k].total += e.amount || 0;
                  });
                  const keys = Object.keys(catTotals).sort((a, b) => a.localeCompare(b));
                  if (keys.length === 0) return (<div className="px-3 py-3 text-[11px] text-zinc-400">{t('noData') || 'No categories'}</div>);
                  const toggleCat = (k) => {
                    setFilterCategories(prev => prev.includes(k)
                      ? prev.filter(c => c !== k)
                      : [...prev, k]);
                  };
                  return keys.map(k => {
                    const checked = filterCategories.includes(k);
                    return (
                      <label
                        key={k}
                        data-testid={`expenses-cat-${k}`}
                        className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-xs text-left border-l-2 border-t border-zinc-100 cursor-pointer select-none transition-colors ${checked
                          ? 'border-l-zinc-900 bg-zinc-100 text-zinc-900 font-semibold'
                          : 'border-l-transparent hover:bg-zinc-50 text-zinc-700'}`}
                      >
                        <span className="flex items-center gap-2 min-w-0">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleCat(k)}
                            className="rounded-none accent-zinc-900"
                            data-testid={`expenses-cat-chk-${k}`}
                          />
                          <span className={`w-1.5 h-1.5 rounded-full ${parseInt(k) >= 300 ? 'bg-amber-500' : 'bg-blue-500'}`} />
                          <span className="truncate">{getCatLabel(k)}</span>
                        </span>
                        <span className="font-mono text-zinc-500 text-[10px] ml-2 shrink-0">€{catTotals[k].total.toFixed(0)}</span>
                      </label>
                    );
                  });
                })()}
              </div>
            </div>
          </aside>

          {/* MAIN EXPENSES TABLE — always list mode, sorted by date desc */}
          <main className="flex-1 min-w-0" data-testid="expenses-main">

            <div className="hidden md:block bg-white border border-zinc-200 overflow-x-auto">
              {filteredExpenses.length === 0 ? (
                <div className="p-12 text-center text-zinc-500">{t('noExpenses')}</div>
              ) : (
                <table className="excel-table" data-testid="expenses-table">
                  <thead>
                    <tr>
                      <th className="text-xs">{t('expenseDate') || 'Date'}</th>
                      <th className="text-xs">{t('expenseCategory') || 'Category'}</th>
                      <th className="text-xs">{t('supplierSource') || 'Supplier / Source'}</th>
                      <th className="text-xs">{t('description') || 'Description'}</th>
                      <th className="text-xs text-right">{t('amountWithoutVat') || 'Net'}</th>
                      <th className="text-xs text-right">{t('vat') || 'VAT'}</th>
                      <th className="text-xs text-right">{t('totalWithVat') || 'Total'}</th>
                      <th className="text-xs">{t('notes') || 'Notes'}</th>
                      <th className="w-40 text-xs">{t('edit')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredExpenses.map((expense) => {
                      const hasVatBreakdown = expense.net_amount != null && expense.vat_amount != null;
                      const net = hasVatBreakdown ? expense.net_amount : null;
                      const vat = hasVatBreakdown ? expense.vat_amount : null;
                      const supplier = expense.vendor_name || '';
                      const description = expense.description || '';
                      return (
                        <tr key={expense.expense_id} data-testid={`expense-row-${expense.expense_id}`}>
                          <td className="font-mono text-sm whitespace-nowrap">
                            {expense.date}
                            {expense.billing_period_start && expense.billing_period_end && (
                              <div className="text-[10px] text-zinc-400 mt-0.5">{expense.billing_period_start} — {expense.billing_period_end}</div>
                            )}
                          </td>
                          <td>
                            <span className={`px-2 py-1 text-[11px] font-mono border ${getCategoryColor(expense.category)}`}>{getCatLabel(expense.category)}</span>
                          </td>
                          <td className="text-sm text-zinc-700">{supplier || <span className="text-zinc-300">&mdash;</span>}</td>
                          <td className="text-sm text-zinc-700 max-w-[220px] truncate" title={description}>{description || <span className="text-zinc-300">&mdash;</span>}</td>
                          <td className="font-mono text-right text-sm text-zinc-600">{net != null ? `€${net.toFixed(2)}` : <span className="text-zinc-300">&mdash;</span>}</td>
                          <td className="font-mono text-right text-sm text-amber-700">{vat != null ? `€${vat.toFixed(2)}` : <span className="text-zinc-300">&mdash;</span>}</td>
                          <td className="font-mono font-semibold text-right" data-testid={`total-amount-${expense.expense_id}`}>&euro;{(expense.amount || 0).toFixed(2)}</td>
                          <td className="text-xs text-zinc-500 max-w-[180px] truncate" title={expense.notes || ''}>{expense.notes || <span className="text-zinc-300">&mdash;</span>}</td>
                          <td><ExpenseActions expense={expense} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Cards - Mobile */}
            <div className="md:hidden space-y-3">
              {filteredExpenses.length === 0 ? (
                <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-500">{t('noExpenses')}</div>
              ) : (
                filteredExpenses.map((expense) => (
                  <div key={expense.expense_id} className="mobile-card" data-testid={`expense-card-${expense.expense_id}`}>
                    <div className="mobile-card-header">
                      <div>
                        <h3 className="mobile-card-title">{expense.building_name}</h3>
                        <span className={`px-2 py-0.5 text-xs font-mono border ${getCategoryColor(expense.category)}`}>{getCatLabel(expense.category)}</span>
                      </div>
                    </div>
                    <div className="mt-3 space-y-2 text-sm">
                      {expense.vendor_name && (<div className="flex justify-between"><span className="text-zinc-500">{t('supplierSource') || 'Supplier'}</span><span>{expense.vendor_name}</span></div>)}
                      {expense.description && (<div className="flex justify-between"><span className="text-zinc-500">{t('description')}</span><span className="text-right max-w-[60%] truncate">{expense.description}</span></div>)}
                      <div className="flex justify-between"><span className="text-zinc-500">{t('amount')}</span><span className="font-mono font-semibold">&euro;{(expense.amount || 0).toFixed(2)}</span></div>
                      <div className="flex justify-between"><span className="text-zinc-500">{t('expenseDate')}</span><span className="font-mono">{expense.date}</span></div>
                      {expense.notes && (<p className="text-zinc-500 text-xs pt-1 border-t border-zinc-100">{expense.notes}</p>)}
                    </div>
                    <div className="flex gap-2 mt-4 pt-3 border-t border-zinc-100">
                      <Button size="sm" onClick={() => openAddPayment(expense)} className="flex-1 bg-blue-600 hover:bg-blue-700 rounded-none" data-testid={`mobile-add-payment-${expense.expense_id}`}>
                        <CreditCard size={16} className="mr-1" />{t('addPayment')}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openViewPayments(expense)} className="flex-1 rounded-none" data-testid={`mobile-view-payments-${expense.expense_id}`}>
                        <Eye size={14} className="mr-1" />{t('viewPayments')}
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>

          </main>
        </div>

        {/* GROUP BY CATEGORY VIEW removed in Phase 418 — categories now drive a sidebar filter. */}

        {/* ===================== DIALOGS ===================== */}

        {/* Add/Edit Expense Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {editingExpense ? t('editExpense') : t('addExpense')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>{t('buildings')}</Label>
                <Select value={formData.building_id} onValueChange={(val) => setFormData({ ...formData, building_id: val })} required>
                  <SelectTrigger className="mt-1 rounded-none" data-testid="expense-building-select"><SelectValue placeholder={t('buildings')} /></SelectTrigger>
                  <SelectContent>
                    {buildings.map(b => <SelectItem key={b.building_id} value={b.building_id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t('expenseCategory')}</Label>
                <Select value={formData.category} onValueChange={(val) => setFormData({ ...formData, category: val, expense_type: CATEGORY_TO_TYPE[val] || 'other' })}>
                  <SelectTrigger className="mt-1 rounded-none" data-testid="expense-category-select"><SelectValue placeholder={t('expenseCategory')} /></SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>{t('monthlyExpenses')}</SelectLabel>
                      {MONTHLY_CATEGORIES.map(c => <SelectItem key={c} value={c}>{getCatLabel(c)}</SelectItem>)}
                      {allCategories.filter(c => !MONTHLY_CATEGORIES.includes(c) && !CAPITAL_CATEGORIES.includes(c) && parseInt(c) >= 200 && parseInt(c) < 300).map(c =>
                        <SelectItem key={c} value={c}>{getCatLabel(c)}</SelectItem>
                      )}
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel>{t('capitalExpenses')}</SelectLabel>
                      {CAPITAL_CATEGORIES.map(c => <SelectItem key={c} value={c}>{getCatLabel(c)}</SelectItem>)}
                      {allCategories.filter(c => !MONTHLY_CATEGORIES.includes(c) && !CAPITAL_CATEGORIES.includes(c) && parseInt(c) >= 300).map(c =>
                        <SelectItem key={c} value={c}>{getCatLabel(c)}</SelectItem>
                      )}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t('amount')} (&euro;)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                  className="mt-1 rounded-none font-mono"
                  required
                  disabled={!!editingExpense}
                  data-testid="expense-amount-input"
                />
                {editingExpense && (
                  <p className="text-xs text-zinc-400 mt-1">{t('amount')} cannot be changed. Use payments to track amounts.</p>
                )}
              </div>

              {/* Phase 418 — Optional VAT breakdown. When both Net & VAT are
                  provided, Total is computed automatically as Net + VAT. */}
              <div className="grid grid-cols-2 gap-3" data-testid="expense-vat-breakdown">
                <div>
                  <Label className="text-xs">{t('amountWithoutVat') || 'Amount Without VAT'} (&euro;)</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={formData.net_amount}
                    onChange={(e) => setFormData({ ...formData, net_amount: e.target.value })}
                    className="mt-1 rounded-none font-mono"
                    disabled={!!editingExpense}
                    placeholder={t('optional') || 'Optional'}
                    data-testid="expense-net-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">{t('vat') || 'VAT'} (&euro;)</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={formData.vat_amount}
                    onChange={(e) => setFormData({ ...formData, vat_amount: e.target.value })}
                    className="mt-1 rounded-none font-mono"
                    disabled={!!editingExpense}
                    placeholder={t('optional') || 'Optional'}
                    data-testid="expense-vat-input"
                  />
                </div>
              </div>
              {!editingExpense && formData.net_amount !== '' && formData.vat_amount !== '' && (
                <p className="text-[11px] text-zinc-500 -mt-2">
                  {t('totalWithVat') || 'Total With VAT'}: <span className="font-mono">€{((parseFloat(formData.net_amount) || 0) + (parseFloat(formData.vat_amount) || 0)).toFixed(2)}</span>
                </p>
              )}

              <div>
                <Label>{t('description') || 'Description'}</Label>
                <Input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="mt-1 rounded-none"
                  placeholder={t('optional') || 'Optional'}
                  data-testid="expense-description-input"
                />
              </div>

              {/* Billing/Service Period — Phase 412 collapsible */}
              {showBillingPeriod ? (
                <div className="border-t border-zinc-200 pt-4 space-y-2" data-testid="billing-period-section">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold block">{t('servicePeriod')}</Label>
                    <button
                      type="button"
                      onClick={() => {
                        setShowBillingPeriod(false);
                        setFormData({ ...formData, billing_period_start: '', billing_period_end: '' });
                      }}
                      className="text-[11px] text-zinc-500 hover:text-zinc-900 underline-offset-2 hover:underline"
                      data-testid="billing-period-remove-btn"
                    >{t('remove') || 'Remove'}</button>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-zinc-500">{t('dateFrom')}</Label>
                      <DatePickerDMY value={formData.billing_period_start} onChange={v => setFormData({ ...formData, billing_period_start: v })} className="mt-1 font-mono" data-testid="billing-start-input" />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-500">{t('dateTo')}</Label>
                      <DatePickerDMY value={formData.billing_period_end} onChange={v => setFormData({ ...formData, billing_period_end: v })} className="mt-1 font-mono" data-testid="billing-end-input" />
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowBillingPeriod(true)}
                  className="border-t border-zinc-200 pt-3 -mt-1 flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 transition-colors w-fit"
                  data-testid="billing-period-toggle-btn"
                >
                  <Plus size={13} />
                  <span>{t('addBillingPeriod') || 'Add Billing Period'}</span>
                </button>
              )}

              <div>
                <Label>{t('notes')}</Label>
                <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="mt-1 rounded-none" rows={2} data-testid="expense-notes-input" />
              </div>

              {!editingExpense && (
                <div>
                  <Label>{t('paymentSource')}</Label>
                  <Select value={formData.payment_source} onValueChange={(val) => setFormData({ ...formData, payment_source: val })}>
                    <SelectTrigger className="mt-1 rounded-none" data-testid="expense-payment-source-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t('unpaidLeaveOpen')}</SelectItem>
                      <SelectItem value="cash">{t('cash')}</SelectItem>
                      <SelectItem value="bank">{t('bank')}</SelectItem>
                      <SelectItem value="reserve_fund">{t('reserveFund')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-400 mt-1">{t('paymentSourceHint')}</p>
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-expense-btn">
                  <CurrencyEur size={18} className="mr-2" />{t('save')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Expense Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deleteExpense')}</AlertDialogTitle>
              <AlertDialogDescription>{t('confirmDelete')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 rounded-none" data-testid="confirm-delete-expense-btn">{t('delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Phase 717 — Save Current View Dialog */}
        <Dialog open={saveViewOpen} onOpenChange={setSaveViewOpen}>
          <DialogContent className="sm:max-w-md" data-testid="save-view-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('saveCurrentView') || 'Save Current View'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-zinc-500 block mb-1">
                  {t('viewName') || 'View Name'}
                </label>
                <Input
                  value={saveViewName}
                  onChange={(e) => setSaveViewName(e.target.value)}
                  placeholder={t('viewNamePlaceholder') || 'e.g. Monthly Cleaning, Q1 Expenses'}
                  className="rounded-none border-zinc-300"
                  maxLength={80}
                  autoFocus
                  data-testid="save-view-name-input"
                  onKeyDown={(e) => { if (e.key === 'Enter') saveCurrentAsView(); }}
                />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={saveViewDefault}
                  onChange={(e) => setSaveViewDefault(e.target.checked)}
                  className="accent-zinc-900"
                  data-testid="save-view-default-toggle"
                />
                <span>{t('setAsDefaultView') || 'Set as default (loads automatically when opening Expenses)'}</span>
              </label>
              <div className="text-[11px] text-zinc-500 bg-zinc-50 border border-zinc-200 p-2">
                {t('savedFiltersSummary') || 'The current Building, Category, Date range and Search filters will be saved with this view.'}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setSaveViewOpen(false)} data-testid="save-view-cancel-btn">
                {t('cancel') || 'Cancel'}
              </Button>
              <Button className="bg-zinc-950 rounded-none" onClick={saveCurrentAsView} data-testid="save-view-confirm-btn">
                {t('save') || 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Phase 717 — Rename Saved View Dialog */}
        <Dialog open={renameViewOpen} onOpenChange={setRenameViewOpen}>
          <DialogContent className="sm:max-w-md" data-testid="rename-view-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('renameView') || 'Rename View'}</DialogTitle>
            </DialogHeader>
            <Input
              value={renameViewName}
              onChange={(e) => setRenameViewName(e.target.value)}
              className="rounded-none border-zinc-300"
              maxLength={80}
              autoFocus
              data-testid="rename-view-name-input"
              onKeyDown={(e) => { if (e.key === 'Enter') renameActiveView(); }}
            />
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setRenameViewOpen(false)} data-testid="rename-view-cancel-btn">
                {t('cancel') || 'Cancel'}
              </Button>
              <Button className="bg-zinc-950 rounded-none" onClick={renameActiveView} data-testid="rename-view-confirm-btn">
                {t('save') || 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Phase 717 — Delete Saved View confirmation */}
        <AlertDialog open={deleteViewConfirmOpen} onOpenChange={setDeleteViewConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deleteView') || 'Delete View'}</AlertDialogTitle>
              <AlertDialogDescription>
                {activeView ? (`${t('confirmDeleteView') || 'Delete this saved view?'} (${activeView.name})`) : (t('confirmDeleteView') || 'Delete this saved view?')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none" data-testid="delete-view-cancel-btn">{t('cancel') || 'Cancel'}</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDeleteActiveView} className="bg-red-600 rounded-none" data-testid="delete-view-confirm-btn">
                {t('delete') || 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Add Payment Dialog */}
        <Dialog open={addPaymentDialogOpen} onOpenChange={setAddPaymentDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('addPayment')}</DialogTitle>
            </DialogHeader>
            {selectedExpense && (
              <div className="bg-zinc-50 border border-zinc-200 p-3 mb-2 space-y-1">
                <p className="text-sm font-medium">{selectedExpense.building_name}</p>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">{t('totalAmount')}:</span>
                  <span className="font-mono font-semibold">&euro;{(selectedExpense.amount || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">{t('paidAmount')}:</span>
                  <span className="font-mono text-emerald-700">&euro;{(selectedExpense.total_paid || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm font-semibold">
                  <span className="text-zinc-500">{t('expenseRemainingBalance')}:</span>
                  <span className={`font-mono ${(selectedExpense.remaining_balance || 0) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    &euro;{(selectedExpense.remaining_balance ?? selectedExpense.amount ?? 0).toFixed(2)}
                  </span>
                </div>
              </div>
            )}
            <form onSubmit={handleAddPayment} className="space-y-4">
              <div>
                <Label>{t('amount')} (&euro;)</Label>
                <Input
                  type="number" step="0.01" min="0.01"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  className="mt-1 rounded-none font-mono"
                  required
                  placeholder="0.00"
                  data-testid="payment-amount-input"
                />
              </div>
              <div>
                <Label>{t('date')}</Label>
                <DatePickerDMY value={paymentForm.date} onChange={v => setPaymentForm({ ...paymentForm, date: v })} className="mt-1 font-mono" required data-testid="payment-date-input" />
              </div>
              <div>
                <Label>{t('paymentSource')}</Label>
                <Select value={paymentForm.payment_source} onValueChange={(val) => setPaymentForm({ ...paymentForm, payment_source: val })}>
                  <SelectTrigger className="mt-1 rounded-none" data-testid="payment-source-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">{t('cash')}</SelectItem>
                    <SelectItem value="bank">{t('bank')}</SelectItem>
                    <SelectItem value="reserve_fund">{t('reserveFund')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('notes')}</Label>
                <Input
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  className="mt-1 rounded-none"
                  placeholder={t('notes') + '...'}
                  data-testid="payment-notes-input"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setAddPaymentDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-payment-btn">
                  <CreditCard size={18} className="mr-2" />{t('addPayment')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Payments Dialog */}
        <Dialog open={viewPaymentsDialogOpen} onOpenChange={setViewPaymentsDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('paymentHistory')}</DialogTitle>
            </DialogHeader>
            {selectedExpense && (
              <div className="bg-zinc-50 border border-zinc-200 p-3 mb-2 space-y-1">
                <p className="text-sm font-medium">{selectedExpense.building_name} &mdash; {getCatLabel(selectedExpense.category)}</p>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">{t('totalAmount')}:</span>
                  <span className="font-mono font-semibold">&euro;{(selectedExpense.amount || 0).toFixed(2)}</span>
                </div>
              </div>
            )}
            {loadingPayments ? (
              <div className="py-8 text-center text-zinc-500">{t('loading')}</div>
            ) : expensePayments.length === 0 ? (
              <div className="py-8 text-center text-zinc-400">{t('noPayments')}</div>
            ) : (
              <div className="border border-zinc-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-zinc-50 border-b border-zinc-200">
                      <th className="text-left px-3 py-2 font-medium text-zinc-600">{t('date')}</th>
                      <th className="text-left px-3 py-2 font-medium text-zinc-600">{t('paymentSource')}</th>
                      <th className="text-right px-3 py-2 font-medium text-zinc-600">{t('amount')}</th>
                      <th className="text-left px-3 py-2 font-medium text-zinc-600">{t('notes')}</th>
                      <th className="w-12 px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {expensePayments.map((payment) => (
                      <tr key={payment.payment_id} className="border-b border-zinc-100 last:border-0" data-testid={`payment-row-${payment.payment_id}`}>
                        <td className="px-3 py-2 font-mono">{payment.date}</td>
                        <td className="px-3 py-2">
                          <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase border ${payment.payment_source === 'cash' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : payment.payment_source === 'bank' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-violet-50 text-violet-700 border-violet-200'}`}>
                            {t(payment.payment_source || 'cash')}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono font-semibold text-right text-emerald-700">&euro;{(payment.amount || 0).toFixed(2)}</td>
                        <td className="px-3 py-2 text-zinc-600">{payment.notes || <span className="text-zinc-300">&mdash;</span>}</td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => confirmDeletePayment(payment)}
                            className="p-1.5 hover:bg-red-50 text-red-500 transition-colors"
                            title={t('deletePayment')}
                            data-testid={`delete-payment-${payment.payment_id}`}
                          >
                            <Trash size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-zinc-50 border-t border-zinc-200">
                      <td className="px-3 py-2 font-semibold text-sm">{t('total')}</td>
                      <td className="px-3 py-2 font-mono font-bold text-right text-emerald-700">
                        &euro;{expensePayments.reduce((sum, p) => sum + (p.amount || 0), 0).toFixed(2)}
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setViewPaymentsDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
              <Button onClick={() => { setViewPaymentsDialogOpen(false); if (selectedExpense) openAddPayment(selectedExpense); }} className="bg-zinc-950 rounded-none" data-testid="add-payment-from-history-btn">
                <CreditCard size={18} className="mr-2" />{t('addPayment')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Payment Confirmation */}
        <AlertDialog open={deletePaymentDialogOpen} onOpenChange={setDeletePaymentDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deletePayment')}</AlertDialogTitle>
              <AlertDialogDescription>{t('confirmDeletePayment')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeletePayment} className="bg-red-600 rounded-none" data-testid="confirm-delete-payment-btn">{t('delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Rename Category Dialog */}
        <Dialog open={renameCatDialogOpen} onOpenChange={setRenameCatDialogOpen}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('renameCategory')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-zinc-500">{t('category')}: <span className="font-mono font-semibold">{renamingCat}</span></p>
              <Input value={renameCatValue} onChange={(e) => setRenameCatValue(e.target.value)} className="rounded-none" placeholder={t('renameCategory')} data-testid="rename-cat-input" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRenameCatDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
              <Button onClick={handleRenameCat} className="bg-zinc-950 rounded-none" data-testid="save-rename-cat-btn">{t('save')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Category Dialog */}
        <AlertDialog open={deleteCatDialogOpen} onOpenChange={setDeleteCatDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deleteCategory')}</AlertDialogTitle>
              <AlertDialogDescription>{t('deleteCategoryWarning')}</AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-2">
              <Label className="text-sm">{t('moveExpensesTo')}</Label>
              <Select value={moveCatTarget} onValueChange={setMoveCatTarget}>
                <SelectTrigger className="mt-1 rounded-none" data-testid="move-cat-select"><SelectValue placeholder={t('deleteAnyway')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('deleteAnyway')}</SelectItem>
                  {[...MONTHLY_CATEGORIES, ...CAPITAL_CATEGORIES, ...allCategories].filter((c, i, a) => a.indexOf(c) === i && c !== deletingCat).map(c => (
                    <SelectItem key={c} value={c}>{getCatLabel(c)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteCat} className="bg-red-600 rounded-none" data-testid="confirm-delete-cat-btn">{t('delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Manage Categories Dialog */}
        <Dialog open={manageCatOpen} onOpenChange={setManageCatOpen}>
          <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('manageCategories')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Add New Category */}
              <div className="border border-zinc-200 p-4 bg-zinc-50/50">
                <Label className="text-sm font-semibold mb-2 block">{t('addCategory')}</Label>
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Input value={newCatName} onChange={(e) => setNewCatName(e.target.value)} className="rounded-none" placeholder={t('categoryName')} data-testid="new-cat-name-input" />
                  </div>
                  <Select value={newCatGroup} onValueChange={setNewCatGroup}>
                    <SelectTrigger className="w-[140px] rounded-none" data-testid="new-cat-group-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">{t('monthlyExpenses')}</SelectItem>
                      <SelectItem value="capital">{t('capitalExpenses')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={handleAddCategory} className="bg-zinc-950 rounded-none" data-testid="add-cat-btn">
                    <Plus size={16} className="mr-1" />{t('add')}
                  </Button>
                </div>
              </div>

              {/* Monthly Categories List */}
              <div>
                <h4 className="text-sm font-semibold mb-2">{t('monthlyExpenses')}</h4>
                <div className="border border-zinc-200 divide-y divide-zinc-100">
                  {[...MONTHLY_CATEGORIES, ...allCategories.filter(c => !MONTHLY_CATEGORIES.includes(c) && !CAPITAL_CATEGORIES.includes(c) && parseInt(c) >= 200 && parseInt(c) < 300)].map(cat => (
                    <div key={cat} className="flex items-center justify-between px-3 py-2 hover:bg-zinc-50">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-zinc-400">{cat}</span>
                        <span className="text-sm">{getCatLabel(cat)}</span>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => { openRenameCat(cat); setManageCatOpen(false); }} className="p-1.5 hover:bg-zinc-100 text-zinc-500 transition-colors" data-testid={`manage-rename-${cat}`}>
                          <PencilSimple size={14} />
                        </button>
                        <button onClick={() => { openDeleteCat(cat); setManageCatOpen(false); }} className="p-1.5 hover:bg-red-50 text-red-500 transition-colors" data-testid={`manage-delete-${cat}`}>
                          <Trash size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Capital Categories List */}
              <div>
                <h4 className="text-sm font-semibold mb-2">{t('capitalExpenses')}</h4>
                <div className="border border-zinc-200 divide-y divide-zinc-100">
                  {[...CAPITAL_CATEGORIES, ...allCategories.filter(c => !MONTHLY_CATEGORIES.includes(c) && !CAPITAL_CATEGORIES.includes(c) && parseInt(c) >= 300)].map(cat => (
                    <div key={cat} className="flex items-center justify-between px-3 py-2 hover:bg-zinc-50">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-zinc-400">{cat}</span>
                        <span className="text-sm">{getCatLabel(cat)}</span>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => { openRenameCat(cat); setManageCatOpen(false); }} className="p-1.5 hover:bg-zinc-100 text-zinc-500 transition-colors" data-testid={`manage-rename-${cat}`}>
                          <PencilSimple size={14} />
                        </button>
                        <button onClick={() => { openDeleteCat(cat); setManageCatOpen(false); }} className="p-1.5 hover:bg-red-50 text-red-500 transition-colors" data-testid={`manage-delete-${cat}`}>
                          <Trash size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
